import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useT, useI18n } from "@/context/I18nContext";
import { useAuth, isSuperAdmin } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Globe, ShieldCheck, User as UserIcon } from "lucide-react";

export default function AdminSettings() {
  const t = useT();
  const { lang, setLang } = useI18n();
  const { user, platform, refresh } = useAuth();
  const { roles, orgName } = useOutletContext();

  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.patch("/me/account", { name });
      toast.success(t("common.saved"));
      await refresh();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const saveLang = async (v) => {
    setLang(v);
    try { await api.patch("/profile/language", { language: v }); toast.success(t("common.saved")); } catch {}
  };

  const orgId = platform?.organization_id || platform?.org_id || platform?.org?.id;
  const [orgNameEdit, setOrgNameEdit] = useState(orgName || "");
  const [savingOrg, setSavingOrg] = useState(false);

  useEffect(() => { setOrgNameEdit(orgName || ""); }, [orgName]);

  const saveOrg = async () => {
    if (!orgId) return;
    setSavingOrg(true);
    try {
      await api.patch(`/platform/organizations/${orgId}`, { name: orgNameEdit });
      toast.success(t("common.saved"));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingOrg(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("common.settings")} title={t("common.settings")} subtitle="Your profile and organisation preferences." />

      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8" data-testid="admin-settings-profile">
        <div className="flex items-center gap-2 mb-4">
          <UserIcon className="h-4 w-4 text-sage-600" /> <h2 className="font-heading text-lg text-ink-900">{t("settings.profile")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>{t("common.name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-cream-50" data-testid="admin-settings-name" />
          </div>
          <div>
            <Label>{t("common.email")}</Label>
            <Input value={user?.email || ""} disabled className="mt-1.5 h-11 rounded-xl bg-cream-50" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveProfile} disabled={busy} className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5" data-testid="admin-settings-save-profile">
            {busy ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </section>

      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8" data-testid="admin-settings-language">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="h-4 w-4 text-sage-600" /> <h2 className="font-heading text-lg text-ink-900">{t("settings.languagePref")}</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={() => saveLang("en")} data-testid="admin-lang-en" className={`rounded-full px-4 py-2 text-sm border ${lang === "en" ? "bg-sage-500 text-white border-sage-500" : "bg-white border-border text-ink-700"}`}>{t("common.english")}</button>
          <button onClick={() => saveLang("nl")} data-testid="admin-lang-nl" className={`rounded-full px-4 py-2 text-sm border ${lang === "nl" ? "bg-sage-500 text-white border-sage-500" : "bg-white border-border text-ink-700"}`}>{t("common.dutch")}</button>
        </div>
      </section>

      {orgId && (
        <section className="rounded-3xl bg-white shadow-card p-6 md:p-8" data-testid="admin-settings-org">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-sage-600" /> <h2 className="font-heading text-lg text-ink-900">{t("nav.organisation")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>{t("common.name")}</Label>
              <Input value={orgNameEdit} onChange={(e) => setOrgNameEdit(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-cream-50" data-testid="admin-settings-org-name" disabled={!isSuperAdmin(roles)} />
            </div>
          </div>
          {isSuperAdmin(roles) && (
            <div className="mt-4 flex justify-end">
              <Button onClick={saveOrg} disabled={savingOrg} className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5" data-testid="admin-settings-save-org">
                {savingOrg ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          )}
        </section>
      )}

      <section className="rounded-3xl bg-white shadow-card p-6 md:p-8" data-testid="admin-settings-privacy">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-4 w-4 text-sage-600" /> <h2 className="font-heading text-lg text-ink-900">Privacy</h2>
        </div>
        <p className="text-sm text-ink-700">
          {lang === "nl"
            ? "Inner Compass toont HR en teamleiders alleen geaggregeerde en geanonimiseerde gegevens. Persoonlijke gesprekken, dagboekjes, reflecties en herinneringen blijven privé voor de gebruiker. Bij teams met minder dan 5 actieve gebruikers wordt geen data getoond."
            : "Inner Compass only shows HR and team leaders aggregated, anonymised data. Personal conversations, journals, reflections and memories always stay private to the individual. Teams with fewer than 5 active users show no data at all."}
        </p>
      </section>
    </div>
  );
}
